/**
 * AlignmentService — Full Horizontal Alignment + Stationing Engine
 *
 * Supports:
 * - Create alignment from polyline (GeoJSON)
 * - Compute station (chainage) at any point along alignment
 * - PI (point of intersection), tangent, curve data (simple circular for now)
 * - Sample points at regular station intervals
 * - Vertical profile generation (station vs elevation)
 * - Grade calculations
 *
 * Used by Surveyors (layout) and Civil Engineers (design).
 */

import length from '@turf/length';
import { lineString, point } from '@turf/helpers';
import distance from '@turf/distance';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import type { Feature, LineString, Point } from 'geojson';

export interface AlignmentPoint {
  station: number; // chainage in meters
  easting: number;
  northing: number;
  elevation?: number;
}

export interface AlignmentCurve {
  piIndex: number;
  radius: number; // circular curve radius (m). 0 = no curve
  spiralLength?: number; // length of entry/exit spiral (clothoid approx)
}

export interface VerticalCurve {
  station: number; // station at PVI (point of vertical intersection)
  length: number; // length of vertical curve (m)
  incomingGrade: number; // percent, e.g. 2.5 for +2.5%
  outgoingGrade: number;
}

export interface Alignment {
  id: string;
  name: string;
  geometry: LineString;
  totalLength: number;
  stations: AlignmentPoint[];
  pis: Array<{ station: number; point: [number, number] }>;
  curves: AlignmentCurve[]; // curves defined at PIs
  verticalCurves?: VerticalCurve[];
}

export interface ProfilePoint {
  station: number;
  elevation: number;
  grade?: number; // percent
}

export class AlignmentService {
  /**
   * Create a full alignment from a GeoJSON LineString feature.
   * Computes stations every `interval` meters + at vertices.
   */
  createAlignment(
    lineFeature: Feature<LineString>,
    name: string = 'New Alignment',
    interval: number = 20,
    curves: AlignmentCurve[] = []
  ): Alignment {
    if (!lineFeature?.geometry?.coordinates || lineFeature.geometry.coordinates.length < 2) {
      // Robust empty fallback
      return {
        id: 'aln_empty',
        name,
        geometry: {
          type: 'LineString',
          coordinates: [
            [0, 0],
            [1, 1],
          ],
        },
        totalLength: 0,
        stations: [{ station: 0, easting: 0, northing: 0 }],
        pis: [],
        curves: [],
      };
    }

    const coords = lineFeature.geometry.coordinates as [number, number][];
    let totalLength = length(lineFeature, { units: 'meters' }) || 0;

    const stations: AlignmentPoint[] = [];
    const pis: Array<{ station: number; point: [number, number] }> = [];

    let currentStation = 0;
    stations.push({ station: 0, easting: coords[0][0], northing: coords[0][1] });

    for (let i = 0; i < coords.length - 1; i++) {
      const p1 = coords[i];
      const p2 = coords[i + 1];
      let segLen = this.distance2d(p1, p2);

      // Add PI
      if (i > 0) {
        currentStation += segLen;
        pis.push({ station: currentStation, point: p2 });

        // Apply curve at this PI if defined (circular curve shortens the tangent length)
        const curve = curves.find((c) => c.piIndex === i);
        if (curve && curve.radius > 5) {
          const curveLen = Math.max(0, (Math.PI / 2) * curve.radius * 0.8); // approx arc length
          const spiral = curve.spiralLength || 0;
          // Reduce effective station advance by the curve "shortcut" vs sharp PI
          const shortcut = segLen * 0.15; // simple approximation
          segLen = Math.max(10, segLen - shortcut + curveLen + spiral);
          currentStation += curveLen + spiral - shortcut;
          totalLength += curveLen + spiral - shortcut; // adjust total
        }
      }

      // Sample at interval along the (possibly curved) segment
      const numSamples = Math.floor(segLen / interval);
      for (let s = 1; s <= numSamples; s++) {
        const t = (s * interval) / segLen;
        const x = p1[0] + (p2[0] - p1[0]) * t;
        const y = p1[1] + (p2[1] - p1[1]) * t;
        stations.push({
          station: stations[stations.length - 1].station + interval,
          easting: x,
          northing: y,
        });
      }

      // End of segment / next PI
      stations.push({
        station: currentStation + segLen,
        easting: p2[0],
        northing: p2[1],
      });
    }

    stations[stations.length - 1].station = totalLength;

    return {
      id: 'aln_' + Date.now(),
      name,
      geometry: lineFeature.geometry,
      totalLength: Math.round(totalLength * 100) / 100,
      stations: stations.map((s) => ({ ...s, station: Math.round(s.station * 100) / 100 })),
      pis,
      curves,
    };
  }

  /**
   * Get station and offset for a clicked point (for stakeout / as-built).
   */
  getStationAndOffset(
    alignment: Alignment,
    clickPoint: [number, number]
  ): {
    station: number;
    offset: number; // left/right (positive right when looking in increasing station)
    nearestPoint: [number, number];
  } {
    const line = lineString(alignment.geometry.coordinates);
    const pt = point(clickPoint);

    const snapped = nearestPointOnLine(line, pt, { units: 'meters' });
    const station = snapped.properties.location || 0;

    // Simple offset sign (cross product for side)
    const idx = Math.floor(snapped.properties.index || 0);
    const coords = alignment.geometry.coordinates;
    const p1 = coords[idx];
    const p2 = coords[Math.min(idx + 1, coords.length - 1)];
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const cx = clickPoint[0] - snapped.geometry.coordinates[0];
    const cy = clickPoint[1] - snapped.geometry.coordinates[1];
    const cross = dx * cy - dy * cx;
    const offset = Math.sign(cross) * distance(pt, snapped, { units: 'meters' });

    return {
      station: Math.round(station * 100) / 100,
      offset: Math.round(offset * 100) / 100,
      nearestPoint: snapped.geometry.coordinates as [number, number],
    };
  }

  /**
   * Generate vertical profile points.
   * Supports parabolic vertical curves if alignment.verticalCurves are provided.
   */
  generateVerticalProfile(
    alignment: Alignment,
    elevations?: number[] // one per station or per vertex (from DEM)
  ): ProfilePoint[] {
    if (!alignment?.stations?.length) return [];
    const profile: ProfilePoint[] = [];
    let prevElev: number | null = null;

    const vcs = alignment.verticalCurves || [];

    alignment.stations.forEach((sp, i) => {
      let elev = 100;
      if (elevations && elevations[i] !== undefined) {
        elev = elevations[i];
      } else if (sp.elevation !== undefined) {
        elev = sp.elevation;
      } else {
        // Default design profile (can be overridden by VC)
        elev = 100 + Math.sin(sp.station / 180) * 6 + sp.station / 250;
      }

      // Apply vertical curve if active at this station
      const activeVC = vcs.find(
        (vc) => sp.station >= vc.station - vc.length / 2 && sp.station <= vc.station + vc.length / 2
      );

      if (activeVC) {
        const L = activeVC.length;
        const g1 = activeVC.incomingGrade / 100; // decimal
        const g2 = activeVC.outgoingGrade / 100;
        const a = (g2 - g1) / (2 * L);
        const x = sp.station - (activeVC.station - L / 2); // distance from BVC
        elev = elev + (g1 * x + a * x * x); // parabolic offset from base
      }

      let grade: number | undefined;
      if (prevElev !== null) {
        const ds = sp.station - (alignment.stations[i - 1]?.station || 0);
        if (ds > 0) grade = ((elev - prevElev) / ds) * 100;
      }

      profile.push({ station: sp.station, elevation: Math.round(elev * 100) / 100, grade });
      prevElev = elev;
    });

    return profile;
  }

  /**
   * Sample a point at exact station (for staking or cross-section origin).
   */
  getPointAtStation(alignment: Alignment, station: number): AlignmentPoint | null {
    if (station < 0 || station > alignment.totalLength) return null;

    // Find segment
    for (let i = 0; i < alignment.stations.length - 1; i++) {
      const a = alignment.stations[i];
      const b = alignment.stations[i + 1];
      if (station >= a.station && station <= b.station) {
        const t = (station - a.station) / (b.station - a.station);
        return {
          station,
          easting: a.easting + (b.easting - a.easting) * t,
          northing: a.northing + (b.northing - a.northing) * t,
        };
      }
    }
    return alignment.stations[alignment.stations.length - 1];
  }

  private distance2d(a: [number, number], b: [number, number]): number {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    return Math.hypot(dx, dy);
  }
}

export const alignmentService = new AlignmentService();
